import { useEffect, useState } from 'react';
import { useStepUp } from './useStepUp';

/**
 * Page-level step-up gate. Runs once on mount: if no valid token is present, fires
 * the step-up dialog; `onCancel` runs if the user dismisses it so the page can route
 * them back to a safe location.
 *
 * Once the gate opens, it stays open for the rest of the page's lifetime — even if
 * the token expires while the user is filling out the form. That preserves the form
 * input; an expired token is handled at submit time by the BFF.
 */
export function useRequireStepUp(onCancel: () => void): { ready: boolean } {
  const { getIsValid, requestStepUp } = useStepUp();
  const [ready, setReady] = useState(() => getIsValid());

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only; later token/expiry changes must not re-fire the gate
  useEffect(() => {
    if (ready) {
      return;
    }
    let cancelled = false;
    requestStepUp()
      .then(() => {
        if (!cancelled) {
          setReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          onCancel();
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { ready };
}
